import * as React from "react";
import * as ReactDOM from "react-dom/client";
import type { NavigateFunction, MessageComponents, MessageAndArtifactRenderFunctions } from "@amzn/awsmp-max-agentic-renderer-interface";
import { initializePDFPolyfills } from "@amzn/awsmp-max-ux-patterns/DocumentExporter";
import Alert from "@cloudscape-design/components/alert";
// Main screens (Cloudscape-based) - load immediately
import { Home, Project, Artifact, Compare, Research } from "@amzn/awsmp-max-screens";
import { LoadingSpinner } from "@amzn/awsmp-max-ux-patterns/LoadingSpinner";
import { Routes, Route, useNavigate, useLocation, useParams, HashRouter } from "react-router-dom";
import {
  useMaxService,
  useProject,
  ProjectRenderer,
  AgenticRendererProvider,
  useNavigation,
  withAsyncBreadcrumbs,
  WriteableArtifactRenderer,
  LayoutSplitPanel,
  LayoutHelpPanel,
  parseQueryParams,
  useLayoutManager,
  useBreadcrumbStore,
  useTrackDebugProject,
  usePromptInputAction,
  useLoggerManager,
  useWithAsyncErrors,
  emitToolCallErrorMetric,
  NotFoundErrorBoundary,
} from "@amzn/awsmp-max-agentic-renderer";
import { Layout } from "./Layout";
import { DirectRenderingLayout } from "./DirectRenderingLayout";
import { NotFoundPageWrapper } from "./NotFoundPageWrapper";
import { ChromeClient } from "./utils/ChromeClient";
import { ThemeProvider } from "./ThemeProvider";
import { useAppTranslation } from "@amzn/awsmp-max-translations";
import { PageProps, usePageProps } from "./hooks/usePageProps";
import {
  Message,
  Text,
  AskUser,
  Annotate,
  AnalysisSteps,
  InlineThink,
  PricingAnnotation,
  ProvideRequirementsAnnotation,
  LinkToArtifact,
  NotificationBlock,
  SourcesList,
  RecommendUnits,
} from "@amzn/awsmp-max-agentic-ui-messaging";
import { I18nProviderProps as CloudscapeI18nProviderProps } from "@cloudscape-design/components/i18n";
import { I18nProviderProps as PolarisI18nProviderProps } from "@amzn/awsui-components-react/polaris/i18n";
import { InternationalizationProps } from "@amzn/awsmp-max-translations/lib/types";

// EPO screens (Polaris-based) - load immediately
import { ListConfig, CreateConfig, ViewConfig, GetOffer, DEFAULT_CONFIGURATION_REQUEST } from "@amzn/awsmp-max-screens/aipo";

// Initialize polyfills required for PDF export
initializePDFPolyfills();

// Wrapper to adapt SourcesList to ConnectedAgenticRendererComponent signature
// This bridges pure UI component (SourcesList) with renderer's component pattern (data prop + API props)
const SourcesListWrapper: React.FC<{ data: { citations: any[] } }> = ({ data }) => <SourcesList citations={data.citations} />;

// Message components for GetOffer route
const MESSAGE_COMPONENTS_GETOFFER: MessageComponents = {
  message: Message,
  text: Text,
  analysissteps: AnalysisSteps,
  inlinethink: InlineThink,
  pricingannotation: PricingAnnotation,
  providerequirementsannotation: ProvideRequirementsAnnotation,
  askuser: AskUser, // Unified component (renders on toolInput with both input/output data)
  recommendunits: RecommendUnits, // Base component without onApply — overridden in GetOffer route
  annotate: Annotate, // Unified annotation component (renders on toolInput)
  linktoartifact: LinkToArtifact,
  notification: NotificationBlock, // Unified component for interrupts and errors
  sourceslist: SourcesListWrapper,
};

// Create a root element if it doesn't exist
const rootElement = document.getElementById("root") || document.createElement("div");
if (!rootElement.id) {
  rootElement.id = "root";
  document.body.appendChild(rootElement);
}

// Route parameter types
interface ProjectParams extends Record<string, string | undefined> {
  projectId: string;
}

interface ArtifactParams extends Record<string, string | undefined> {
  projectId: string;
  artifactType: string;
}

// Home route component
function HomeRoute() {
  const pageProps = usePageProps();
  const logger = useLoggerManager();
  logger.updateContext({
    PageType: "AWS-Marketplace-Agents",
    SubPageType: "AWSMP-Max-Home",
  });
  React.useEffect(() => {
    logger.biMetric({
      metricName: "PageHit",
      value: 1,
      unit: "Count",
    });
  }, [logger]);
  return <Home {...(pageProps as any)} />;
}

// Create breadcrumb-wrapped Project component (outside render to prevent recreation)
const ProjectWithBreadcrumbs = withAsyncBreadcrumbs((pageProps: PageProps<ProjectParams>) => (
  <ProjectRenderer projectId={pageProps.params.projectId}>
    {(renderProps) => {
      const project = useProject({ projectId: pageProps.params.projectId });
      const promptInputAction = usePromptInputAction();
      return (
        <Project
          {...pageProps}
          {...renderProps}
          sendMessage={project.sendMessage}
          uploadFiles={project.uploadFiles}
          interruptProject={project.interruptProject}
          promptInputAction={promptInputAction}
          tags={renderProps.renderContext?.tags}
        />
      );
    }}
  </ProjectRenderer>
));

// Project route component
function ProjectRoute() {
  const pageProps = usePageProps<ProjectParams>();
  const logger = useLoggerManager();
  logger.updateContext({
    PageType: "AWS-Marketplace-Agents",
    SubPageType: "AWSMP-Max-Project",
  });
  React.useEffect(() => {
    logger.biMetric({
      metricName: "PageHit",
      value: 1,
      unit: "Count",
    });
  }, [logger]);
  useTrackDebugProject(pageProps.params.projectId);
  return <ProjectWithBreadcrumbs {...pageProps} />;
}

// Create breadcrumb-wrapped Artifact component (outside render to prevent recreation)
const ArtifactWithBreadcrumbs = withAsyncBreadcrumbs((pageProps: PageProps<ArtifactParams>) => (
  <ProjectRenderer projectId={pageProps.params.projectId} artifactType={pageProps.params.artifactType as any}>
    {(renderProps: MessageAndArtifactRenderFunctions) => (
      <Artifact {...pageProps} {...renderProps} artifactType={pageProps.params.artifactType as any} />
    )}
  </ProjectRenderer>
));

// Artifact route component
function ArtifactRoute() {
  const pageProps = usePageProps<ArtifactParams>();
  const logger = useLoggerManager();
  logger.updateContext({
    PageType: "AWS-Marketplace-Agents",
    SubPageType: "AWSMP-Max-Artifact",
  });
  React.useEffect(() => {
    logger.biMetric({
      metricName: "PageHit",
      value: 1,
      unit: "Count",
    });
  }, [logger]);
  useTrackDebugProject(pageProps.params.projectId);
  return <ArtifactWithBreadcrumbs {...pageProps} />;
}

// Compare route component
const CompareRoute: React.FC = () => {
  const pageProps = usePageProps();
  const logger = useLoggerManager();
  logger.updateContext({
    PageType: "AWS-Marketplace-Agents",
    SubPageType: "AWSMP-Max-Compare",
  });
  React.useEffect(() => {
    logger.biMetric({
      metricName: "PageHit",
      value: 1,
      unit: "Count",
    });
  }, [logger]);
  return <Compare {...pageProps} />;
};

// Research route component
const ResearchRoute: React.FC = () => {
  const logger = useLoggerManager();
  logger.updateContext({
    PageType: "AWS-Marketplace-Agents",
    SubPageType: "AWSMP-Max-Research",
  });
  React.useEffect(() => {
    logger.biMetric({
      metricName: "PageHit",
      value: 1,
      unit: "Count",
    });
  }, [logger]);
  const pageProps = usePageProps();
  return <Research {...pageProps} />;
};

// EPO Route Components
const EPOEntryRoute: React.FC = () => {
  const { t } = useAppTranslation();
  const { setBreadcrumbs: setBreadcrumbsStore } = useBreadcrumbStore();
  const { flashbar } = useLayoutManager();
  const pageProps = usePageProps();
  const { logger } = pageProps;
  logger.updateContext({ PageType: "AWSMP-EPO-List-Config" });
  const { withAsyncErrors } = useWithAsyncErrors(logger, {
    onNonFatalError: (error) => emitToolCallErrorMetric(error, logger),
  });

  return (
    <ListConfig t={t as any} setBreadcrumbs={setBreadcrumbsStore} flashbarManager={flashbar} logger={logger} withAsyncErrors={withAsyncErrors} />
  );
};

const EPOCreateRoute: React.FC = () => {
  const { t } = useAppTranslation();
  const maxService = useMaxService();
  const { createProjectWithStreaming } = maxService;
  const [projectId, setProjectId] = React.useState<string | null>(null);
  const [hasError, setHasError] = React.useState(false);
  const pageProps = usePageProps();
  const { productId } = useParams<{ productId?: string }>();
  const { setBreadcrumbs: setBreadcrumbsStore } = useBreadcrumbStore();
  const { flashbar } = useLayoutManager();
  const { logger } = pageProps;
  logger.updateContext({ PageType: "AWSMP-EPO-Create-Config" });
  const { withAsyncErrors } = useWithAsyncErrors(logger, {
    onNonFatalError: (error) => emitToolCallErrorMetric(error, logger),
  });

  React.useEffect(() => {
    if (projectId || hasError) {
      return; // Don't run if we already have a projectId or encountered an error
    }

    withAsyncErrors(
      (async () => {
        createProjectWithStreaming(
          {
            projectName: "EPO Configuration Project",
            agent: {
              agentId: "epo-seller-qualification",
            },
            contentBlocks: [
              {
                updateArtifact: {
                  artifactSection: "epoSellerCreateConfiguration",
                  patches: [
                    {
                      op: "add",
                      path: "",
                      value: {
                        configurationRequest: {
                          ...DEFAULT_CONFIGURATION_REQUEST,
                          productId: productId || "",
                        },
                      },
                    },
                  ],
                },
              },
            ],
            tags: ["EPO_SELLER"],
          },
          undefined,
          (projectId) => {
            if (projectId === undefined) {
              throw new Error("CreateProject failed. No projectId was returned.");
            }
            setProjectId(projectId);
          },
        );
      })(),
    ).catch(() => {
      // Error already handled by withAsyncErrors flashbar
      setHasError(true);
    });
  }, [productId, withAsyncErrors, projectId, hasError]);

  if (hasError) {
    return (
      <Alert type="error" header="Failed to create configuration project">
        Unable to initialize the configuration wizard. Please try again later.
      </Alert>
    );
  }

  if (!projectId) {
    return <LoadingSpinner />;
  }

  return (
    <ProjectRenderer projectId={projectId} artifactType="epoSellerCreateConfiguration">
      {({ renderMessages, sendMessage }) => {
        const renderMessagesWithComponents = () => renderMessages(MESSAGE_COMPONENTS_GETOFFER);
        /* eslint-disable-next-line react-hooks/rules-of-hooks */
        const project = useProject({ projectId });

        return (
          <WriteableArtifactRenderer project={project} artifactTypePath="epoSellerCreateConfiguration" fallback={<LoadingSpinner />}>
            {(writeableArtifact) => {
              const { layout, ...createConfigProps } = pageProps;

              return (
                <CreateConfig
                  {...createConfigProps}
                  productId={productId}
                  t={t as any}
                  writeableArtifact={writeableArtifact}
                  renderMessages={renderMessagesWithComponents}
                  layoutComponents={{ LayoutSplitPanel, LayoutHelpPanel }}
                  sendMessage={sendMessage}
                  setBreadcrumbs={setBreadcrumbsStore}
                  projectId={projectId}
                  flashbarManager={flashbar}
                  logger={logger}
                  withAsyncErrors={withAsyncErrors}
                  turnState={project.renderContext?.projectState}
                />
              );
            }}
          </WriteableArtifactRenderer>
        );
      }}
    </ProjectRenderer>
  );
};

const EPOViewRoute: React.FC = () => {
  const { t } = useAppTranslation();
  const location = useLocation();
  const { setBreadcrumbs: setBreadcrumbsStore } = useBreadcrumbStore();
  const { flashbar } = useLayoutManager();
  const pageProps = usePageProps();
  const { logger } = pageProps;
  logger.updateContext({ PageType: "AWSMP-EPO-View-Config" });
  const { withAsyncErrors } = useWithAsyncErrors(logger, {
    onNonFatalError: (error) => emitToolCallErrorMetric(error, logger),
  });

  // Extract id from path: /epo/epoconfig-xxx or /epo/prod-xxx
  const pathId = location.pathname.replace(/\/$/, "").split("/").pop();

  const sharedProps = {
    t: t as any,
    setBreadcrumbs: setBreadcrumbsStore,
    flashbarManager: flashbar,
    logger,
    withAsyncErrors,
  };

  return <ViewConfig {...sharedProps} entityId={pathId!} />;
};

const EPOOfferRoute: React.FC = () => {
  const { createProject, getProject } = useMaxService();
  const [projectId, setProjectId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<Error | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = parseQueryParams(location.search);
  const productId = queryParams.productId as string;
  const urlProjectId = queryParams.projectId as string | undefined;

  React.useEffect(() => {
    const handleProjectRestoration = async () => {
      if (!productId) {
        return; // Alert will be shown, no need to throw
      }

      // If we have a projectId in URL, try to restore that project first
      if (urlProjectId) {
        try {
          // TODO: Handle missing project response from getProject
          const projectState = await getProject(urlProjectId);

          // Project exists, use it
          setProjectId(projectState.projectId);
          return;
        } catch (error) {
          console.warn("Failed to restore project, creating new one:", {
            projectId: urlProjectId,
            error: error instanceof Error ? error.message : error,
          });
          // Project doesn't exist, fall through to create new project
        }
      }

      // Create project + init artifact + fetch buyer options in single call
      // Server runs: create project → updateArtifact → getBuyerOptions → EndTurn
      // All in one Lambda invocation (was 4 separate calls before)
      const newProjectId = await createProject({
        projectName: `EPO Offer - ${productId}`,
        contentBlocks: [
          {
            updateArtifact: {
              artifactSection: "epoBuyerRequest",
              patches: [{ op: "add", path: "", value: { offerRequest: { productId } } }],
            },
          },
          {
            getExpressPrivateOfferBuyerOptions: { productId },
          },
        ],
        agent: { agentId: "epo-discount" },
        tags: ["EPO_BUYER"],
      });

      setProjectId(newProjectId);

      const searchParams = new URLSearchParams(location.search);
      searchParams.set("projectId", newProjectId);

      navigate({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    };

    if (!projectId && !error) {
      handleProjectRestoration().catch((e) => {
        console.error("Project restoration failed:", e);
        setError(e instanceof Error ? e : new Error(String(e)));
      });
    }
  }, [productId, createProject, getProject, projectId, urlProjectId, error]);

  if (!productId) {
    return <NotFoundPageWrapper />;
  }

  if (error) {
    throw error;
  }

  if (!projectId) {
    return <LoadingSpinner />;
  }

  return <EPOOfferProjectComponent listingId={queryParams.listingId} projectId={projectId} productId={productId} />;
};

// Sub-component for EPO offer route
const EPOOfferProjectComponent: React.FC<{
  projectId: string;
  productId: string;
  listingId: string | undefined;
}> = (props) => {
  const { projectId, productId, listingId } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAppTranslation();

  const pageProps = usePageProps();
  const { logger } = pageProps;
  logger.updateContext({ PageType: "AWSMP-EPO-Request" });
  const { flashbar } = useLayoutManager();
  const { setBreadcrumbs: setBreadcrumbsStore } = useBreadcrumbStore();
  // Stabilize setBreadcrumbs function
  const setBreadcrumbs = React.useCallback(
    (breadcrumbs: { text: string; href: string }[]) => {
      setBreadcrumbsStore(breadcrumbs);
    },
    [setBreadcrumbsStore],
  );

  useTrackDebugProject(projectId);

  const project = useProject({ projectId });
  const { withAsyncErrors } = useWithAsyncErrors(pageProps.logger, {
    onNonFatalError: (error) => emitToolCallErrorMetric(error, pageProps.logger),
  });

  return (
    <WriteableArtifactRenderer project={project} artifactTypePath="epoBuyerRequest" fallback={<LoadingSpinner />}>
      {(writeableArtifact) => (
        <ProjectRenderer projectId={projectId} writeableArtifact={writeableArtifact}>
          {({ renderMessages }) => {
            const { layout, ...getOfferProps } = pageProps;
            const renderMessagesWithComponents = () => renderMessages(MESSAGE_COMPONENTS_GETOFFER);

            return (
              <GetOffer
                projectId={projectId}
                {...getOfferProps}
                writeableArtifact={writeableArtifact}
                renderMessages={renderMessagesWithComponents}
                layoutComponents={{ LayoutSplitPanel, LayoutHelpPanel }}
                flashbarManager={flashbar}
                logger={logger}
                queryParamListingId={listingId}
                setBreadcrumbs={setBreadcrumbs}
                NotFound={NotFoundPageWrapper}
                withAsyncErrors={withAsyncErrors}
                t={t}
                renderContext={project.renderContext}
                validateProductId={() => {
                  if (writeableArtifact.artifact.offerRequest.productId !== productId) {
                    // Wipe invalid projectId and refresh the page.
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.delete("projectId");

                    navigate({
                      pathname: location.pathname,
                      search: searchParams.toString(),
                    });

                    // HashRouter doesn't actually reload on navigate. Force reload.
                    window.location.reload();
                  }
                }}
              />
            );
          }}
        </ProjectRenderer>
      )}
    </WriteableArtifactRenderer>
  );
};

// Layout wrapper component
const LayoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isEPORoute = location.pathname.startsWith("/epo");

  return isEPORoute ? <DirectRenderingLayout startWithNoBreadcrumbs={true}>{children}</DirectRenderingLayout> : <Layout>{children}</Layout>;
};

// Routes component
const AppRoutes: React.FC = () => {
  const { paths } = useNavigation();
  const logger = useLoggerManager();
  const location = useLocation();

  React.useEffect(() => {
    const handler = () => logger.info("Page unloading", { url: window.location.href });
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [logger]);

  return (
    <LayoutWrapper>
      <NotFoundErrorBoundary key={location.pathname}>
        <Routes>
          <Route path={paths.home} element={<HomeRoute />} />
          <Route path={paths.project} element={<ProjectRoute />} />
          <Route path={paths.artifact} element={<ArtifactRoute />} />
          <Route path={paths.compare} element={<CompareRoute />} />
          <Route path={paths.proposal} element={<ResearchRoute />} />
          <Route path={paths.notFound} element={<NotFoundPageWrapper />} />
          <Route path="*" element={<NotFoundPageWrapper />} />

          {/* EPO Routes (formerly AIPO) - Lazy loaded (defers 1.18 MB Polaris bundle) */}
          <Route path={paths.epo} element={<EPOEntryRoute />} />
          <Route path={paths.epoView} element={<EPOViewRoute />} />
          <Route path={paths.epoCreate} element={<EPOCreateRoute />} />
          <Route path={paths.epoOffer} element={<EPOOfferRoute />} />
        </Routes>
      </NotFoundErrorBoundary>
    </LayoutWrapper>
  );
};

type AppWithRouterProps = InternationalizationProps;

// Wrapper that extracts navigate from router and provides to AgenticRenderer
const AppWithRouter: React.FC<AppWithRouterProps> = ({ cloudscapeMessages, polarisMessages }) => {
  const navigate = useNavigate();

  // Create a navigate function that matches our NavigateFunction type
  const navigateFn: NavigateFunction = React.useCallback(
    (to: string, options?: { replace?: boolean; state?: any }) => {
      navigate(to, options);
    },
    [navigate],
  );

  return (
    <AgenticRendererProvider navigate={navigateFn} cloudscapeMessages={cloudscapeMessages} polarisMessages={polarisMessages}>
      <AppRoutes />
    </AgenticRendererProvider>
  );
};

type AppProps = InternationalizationProps;

// Main App component with router
const App: React.FC<AppProps> = ({ cloudscapeMessages, polarisMessages }) => {
  return (
    <ThemeProvider>
      <HashRouter>
        <AppWithRouter cloudscapeMessages={cloudscapeMessages} polarisMessages={polarisMessages} />
      </HashRouter>
    </ThemeProvider>
  );
};

// Initialize chrome and React application
// Removed StrictMode to prevent double rendering during development
export async function initializeApp(
  language: string,
  cloudscapeMessages: readonly CloudscapeI18nProviderProps.Messages[],
  polarisMessages: readonly PolarisI18nProviderProps.Messages[],
) {
  const params = new URLSearchParams(window.location.search);
  if (params.has("awsmpMaxFragmentOverride")) {
    const fragmentOverride = decodeURIComponent(decodeURIComponent(params.get("awsmpMaxFragmentOverride")!));

    window.location.hash = `#/${fragmentOverride}`;
    params.delete("awsmpMaxFragmentOverride");
    window.location.search = params.toString();
  }

  // Dark mode is controlled by ThemeProvider via debug settings
  // Initialize chrome and wait for it to complete
  // This ensures window.awsmpue is available before React renders
  const chromeClient = new ChromeClient(language);
  await chromeClient.waitForInitialization();

  // Render React app immediately (in parallel with chrome initialization)
  ReactDOM.createRoot(rootElement).render(<App cloudscapeMessages={cloudscapeMessages} polarisMessages={polarisMessages} />);
}
